// stores/useFlightBookingStore.ts
import api from "@/app/api";
import { create } from "zustand";
import { SearchQuery } from "./interface/searchFlightInterface";
import {
  TripSegment,
  FlightSearchResponse,
  FlightSearchRequest,
  TotalPriceInfo,
  MultiCityFlightSearchResponse,
  Alert,
  SearchQueryBooking,
  FareComponent,
  AdditionalFareComponent,
} from "@/types/search/flight";
import { SelectedSeat } from "@/types/seatMap";
import { ReadonlyURLSearchParams } from "next/navigation";

interface AirportSuggestion {
  code: string;
  name: string;
  city: string;
  country: string;
}

interface Conditions {
  ffas: any[];
  isa: boolean;
  dob: {
    adobr: boolean;
    cdobr: boolean;
    idobr: boolean;
  };
  iecr: boolean;
  dc: {
    ida: boolean;
    idm: boolean;
  };
  ipa: boolean;
  addOns: {
    isbpa: boolean;
  };
  iss: boolean;
  isBA: boolean;
  st: number;
  sct: string;
  gst: {
    gstappl: boolean;
    igm: boolean;
  };
}

interface SessionObject {
  duration: number;
  is_expired: boolean;
  id: number;
  uid: number;
  details: {
    tripInfos: TripSegment[];
    alerts: Alert[];
    searchQuery: SearchQueryBooking;
    bookingId: string;
    totalPriceInfo: {
      totalFareDetail: {
        fC: FareComponent;
        afC: Partial<AdditionalFareComponent>;
      };
    };
    status: {
      success: boolean;
      httpStatus: number;
    };
    conditions: Conditions;
  };
  booking_id: string;
  updatedAt: string;
  createdAt: string;
  deletedAt: string | null;
}

interface SessionResponse {
  success: boolean;
  message: string;
  session: SessionObject;
}

interface PolicyDetails {
  amount: number;
  policyInfo: string;
  fcs: {
    ARFT: number;
    ARF?: number; // Optional if it might be absent
    ACFT?: number; // Optional if it might be absent
    ACF?: number; // Optional if it might be absent
  };
}

interface FrDetails {
  DATECHANGE: {
    DEFAULT: PolicyDetails;
    BEFORE_DEPARTURE?: PolicyDetails;
    AFTER_DEPARTURE?: PolicyDetails;
  };
  CANCELLATION: {
    DEFAULT: PolicyDetails;
    BEFORE_DEPARTURE?: PolicyDetails;
    AFTER_DEPARTURE?: PolicyDetails;
  };
  NO_SHOW: {
    BEFORE_DEPARTURE?: PolicyDetails;
    AFTER_DEPARTURE?: PolicyDetails;
  };
}

interface TFR {
  NO_SHOW: {
    policyInfo: string;
    st: string;
    et: string;
  }[];
  DATECHANGE: {
    amount: number;
    policyInfo: string;
    fcs: {
      ARFT: number;
      ARF: number;
    };
    st: string;
    et: string;
  }[];
  CANCELLATION: {
    amount: number;
    policyInfo: string;
    fcs: {
      ACF: number;
      ACFT: number;
    };
    st: string;
    et: string;
    ppf: number;
  }[];
  SEAT_CHARGEABLE: {
    policyInfo: string;
    st: string;
    et: string;
  }[];
}


interface DynamicPolicyData {
  [route: string]: {
    fr: FrDetails;
    tfr: TFR
  };
}

export interface FareRuleResponse {
  fareRule: DynamicPolicyData;
}

export interface FareRuleRequest {
  id: string;
  flowType: "SEARCH" | "REVIEW" | "BOOKING_DETAIL";
}

interface AirlineSuggestion {
  code: string;
  name: string;
}

interface FlightBookingStore {
  // Search related
  searchResults: FlightSearchResponse | null;
  flightSearchSuggestions: AirportSuggestion[];
  airlineSearchSuggestions: AirlineSuggestion[];
  selectedFlight: TripSegment | null;
  filteredFlights: FlightSearchResponse | null;
  session: SessionObject | null;
  fareRule: FareRuleResponse | null;
  isFareRuleLoading: boolean;
  isSearchLoading: boolean;
  isFlightSearchSuggestionsLoading: boolean;
  isAirlineSearchSuggestionsLoading: boolean;
  isSessionLoading: boolean;
  error: Error | null;
  receiveError: boolean;
  requestUrl: string;
  selectedReturnFlights: { return: any; onword: any };
  selectedMulticityFlights: any[];
  isLoading: boolean;
  onewaySelectedFare: TotalPriceInfo | null;
  seatMapError: {} | null;
  // New filter state
  activeFilters: {
    price: { min: number; max: number };
    airlines: string[];
    stops: number[];
    departureTime: string[];
  };
  indexValue: number;
  isViewOpen: boolean;
  viewData: any;
  isEmailOpen: boolean;
  shareMails: any;
  isWhatsAppOpen: boolean;

  // Existing actions
  searchFlightSuggestions: (value: string) => Promise<AirportSuggestion[]>;
  searchAirlineSuggestions: (value: string) => Promise<AirlineSuggestion[]>;
  setSearchedFlight: (data: FlightSearchResponse) => Promise<any>;
  setFareRule: (data: any) => Promise<any>;
  setOnewaySelectedFare: (data: TotalPriceInfo | null) => void;
  createSession: (data: string[]) => Promise<any>;
  createRescheduleSession: (priceIds: string[], oldBookingId: string, paxIds?: string[]) => Promise<any>;
  setRequestUrl: (url: string) => void;
  setSelectedFlights: (type: string, data: any) => void;
  setFilteredFlights: (data: any) => void;
  setIsLoading: (loading: boolean) => void;
  setIsFareRuleLoading: (loading: boolean) => void;
  resetStore: () => void;
  setSeatMapError: (error: Error | null) => void;

  // New filter actions
  setActiveFilters: (filters: any) => void;
  applyFilters: (searchResults: FlightSearchResponse) => void;

  // Seat map related
  selectedSeats: SelectedSeat[];
  setSelectedSeat: (seat: SelectedSeat) => void;
  removeSelectedSeat: (segmentId: string, passengerIndex: number) => void;
  clearSelectedSeats: () => void;
  updateMultipleSeats: (seats: SelectedSeat[]) => void;
  getSeatsForPassenger: (passengerIndex: number) => SelectedSeat[];
  getSeatsForSegment: (segmentId: string) => SelectedSeat[];

  // New utility function
  removeEmptyFields: (obj: any) => any;
  setIndexValue: (index: number) => void;
  setIsViewOpen: (isOpen: boolean) => void;
  setIsEmailOpen: (isOpen: boolean) => void;
  setViewData: (data: any) => void;
  setshareMails: (mails: any) => void;
  setIsWhatsAppOpen: (isOpen: boolean) => void;
}

const initialState = {
  // Existing initial state
  searchResults: null,
  flightSearchSuggestions: [],
  airlineSearchSuggestions: [],
  selectedFlight: null,
  filteredFlights: null,
  session: null,
  fareRule: null,
  isSearchLoading: false,
  isFlightSearchSuggestionsLoading: false,
  isAirlineSearchSuggestionsLoading: false,
  isSessionLoading: false,
  error: null,
  receiveError: false,
  requestUrl: "/",
  selectedReturnFlights: { return: {}, onword: {} },
  selectedMulticityFlights: [],
  isLoading: false,
  isFareRuleLoading: false,
  onewaySelectedFare: null,
  seatMapError: null,
  indexValue: 0,
  isViewOpen: false,
  isEmailOpen: false,
  viewData: [],
  shareMails: {},
  isWhatsAppOpen: false,

  // New initial filter state
  activeFilters: {
    price: { min: 0, max: 100000 },
    airlines: [],
    stops: [],
    departureTime: [],
  },

  // Seat map related
  selectedSeats: [],
};

const useFlightBookingStore = create<FlightBookingStore>((set, get) => ({
  ...initialState,

  searchFlightSuggestions: async (value: string) => {
    set({ isFlightSearchSuggestionsLoading: true });
    try {
      const response = await api.get<AirportSuggestion[]>(
        `/flight/airportSuggestions?value=${value}`
      );
      if (!response?.data) throw new Error("Network response was not ok");
      const suggestions = response.data;
      set({
        isFlightSearchSuggestionsLoading: false,
        flightSearchSuggestions: suggestions,
      });
      return suggestions;
    } catch (error: any) {
      set({
        error,
        isFlightSearchSuggestionsLoading: false,
        flightSearchSuggestions: [],
      });
      return [];
    }
  },

  searchAirlineSuggestions: async (value: string) => {
    set({ isAirlineSearchSuggestionsLoading: true });
    try {
      const response = await api.get<AirlineSuggestion[]>(
        `/flight/airlineSuggestions?value=${value}`
      );
      if (!response?.data) throw new Error("Network response was not ok");
      const suggestions = response.data;
      set({
        isAirlineSearchSuggestionsLoading: false,
        airlineSearchSuggestions: suggestions,
      });
      return suggestions;
    } catch (error: any) {
      set({
        error,
        isAirlineSearchSuggestionsLoading: false,
        airlineSearchSuggestions: [],
      });
      return [];
    }
  },

  setSearchedFlight: async (data: FlightSearchResponse) => {
    set({ isSearchLoading: true, error: null });
    try {
      set({ searchResults: data });
    } catch (error: any) {
      set({ error, isSearchLoading: false });
    }
  },

  setOnewaySelectedFare: (data: TotalPriceInfo | null) => {
    set({ onewaySelectedFare: data });
  },

  setFareRule: async (data: FareRuleResponse) => {
    set({ isFareRuleLoading: true, error: null });
    try {
      // Reset the fareRule state before setting new data
      set({ fareRule: null });
      // Set the new fareRule data
      set({ fareRule: data, isFareRuleLoading: false });
    } catch (error: any) {
      set({ error, isFareRuleLoading: false });
    }
  },

  createSession: async (data: string[]) => {
    set({ receiveError: false, isSessionLoading: true, error: null });
    try {
      const response = await api.post<SessionResponse>("/flight/session", data);
      set({
        receiveError: false,
        session: response.data.session,
        isSessionLoading: false,
      });
      return response;
    } catch (error: any) {
      set({ receiveError: true, error, isSessionLoading: false });
    }
  },

  createRescheduleSession: async (priceIds: string[], oldBookingId: string, paxIds?: string[]) => {
    set({ receiveError: false, isSessionLoading: true, error: null });
    try {
      const response = await api.post<SessionResponse>("/flight/reschedule/session", {
        priceIds,
        oldBookingId,
        paxIds,
        priceValidation: true
      });
      set({
        receiveError: false,
        session: response.data.session,
        isSessionLoading: false,
      });
      return response;
    } catch (error: any) {
      set({ receiveError: true, error, isSessionLoading: false });
    }
  },

  setRequestUrl: (url: string) => set({ requestUrl: url }),

  setSelectedFlights: (type: string, data: any) => {
    if (type === "return") {
      set((state: any) => ({
        selectedReturnFlights: { ...state.selectedReturnFlights, return: data },
      }));
    } else if (type === "multicity") {
      set((state: any) => {
        const filteredFlights = state.selectedMulticityFlights.filter(
          (flight: any) => flight.multiCityKey !== data.multiCityKey
        );
        const updatedFlights = [...filteredFlights, data];
        // Sort flights based on multiCityKey
        updatedFlights.sort(
          (a, b) => parseInt(a.multiCityKey) - parseInt(b.multiCityKey)
        );
        return {
          selectedMulticityFlights: updatedFlights,
        };
      });
    } else {
      set((state: any) => ({
        selectedReturnFlights: { ...state.selectedReturnFlights, onword: data },
      }));
    }
  },

  setFilteredFlights: (data: any) => set({ filteredFlights: data }),

  setIsLoading: (loading: boolean) => set({ isLoading: loading }),

  setIsFareRuleLoading: (loading: boolean) =>
    set({ isFareRuleLoading: loading }),

  resetStore: () => set(initialState),

  setActiveFilters: (filters) =>
    set((state) => ({
      activeFilters: {
        ...state.activeFilters,
        ...filters,
      },
    })),

  applyFilters: (searchResults: FlightSearchResponse) => {
    const state = get();
    const { activeFilters } = state;

    let filtered: FlightSearchResponse = searchResults;



    // Apply price filter
    if (activeFilters.price) {
      filtered = {
        ...filtered,
        searchResult: {
          ...filtered.searchResult,
          tripInfos: Object.fromEntries(
            Object.entries(filtered.searchResult.tripInfos).map(
              ([key, flights]) => [
                key,
                // Handle both COMBO and regular flight arrays
                Array.isArray(flights)
                  ? flights.filter((flight: any) => {
                    const price =
                      flight.totalPriceList?.[0]?.fd?.ADULT?.fC?.TF;
                    return (
                      price >= activeFilters.price.min &&
                      price <= activeFilters.price.max
                    );
                  })
                  : flights,
              ]
            )
          ),
        },
      };
    }

    // Apply airline filter
    if (activeFilters.airlines.length > 0) {
      filtered = {
        ...filtered,
        searchResult: {
          ...filtered.searchResult,
          tripInfos: Object.fromEntries(
            Object.entries(filtered.searchResult.tripInfos).map(
              ([key, flights]) => [
                key,
                Array.isArray(flights)
                  ? flights.filter((flight: any) =>
                    flight.sI.some((segment: any) =>
                      activeFilters.airlines.includes(segment.fD.aI.code)
                    )
                  )
                  : flights,
              ]
            )
          ),
        },
      };
    }

    // Apply stops filter
    if (activeFilters.stops.length > 0) {
      filtered = {
        ...filtered,
        searchResult: {
          ...filtered.searchResult,
          tripInfos: Object.fromEntries(
            Object.entries(filtered.searchResult.tripInfos).map(([key, flights]) => [
              key,
              Array.isArray(flights) ? flights.filter((flight: any) =>
                activeFilters.stops.includes(flight.sI.length - 1)
              ) : flights,
            ])
          ),
        },
      };
    }

    set({ filteredFlights: filtered });
  },

  // Seat map related
  setSelectedSeat: (seat: SelectedSeat) =>
    set((state) => {
      try {
        // First, remove any existing seat selection for this passenger+segment 
        const filteredSeats = state.selectedSeats.filter(
          s => !(s.segmentId === seat.segmentId && s.passengerIndex === seat.passengerIndex)
        );

        // Then add the new seat
        return {
          selectedSeats: [...filteredSeats, seat]
        };
      } catch (error) {
        console.error("Error setting seat selection:", error);
        return state; // Return unchanged state on error
      }
    }),

  removeSelectedSeat: (segmentId: string, passengerIndex: number) =>
    set((state) => {
      try {
        return {
          selectedSeats: state.selectedSeats.filter(
            s => !(s.segmentId === segmentId && s.passengerIndex === passengerIndex)
          )
        };
      } catch (error) {
        console.error("Error removing seat selection:", error);
        return state; // Return unchanged state on error
      }
    }),

  clearSelectedSeats: () => set({ selectedSeats: [] }),

  updateMultipleSeats: (seats: SelectedSeat[]) =>
    set((state) => {
      try {
        // Create a map to avoid duplicates (one seat per passenger per segment)
        const seatMap = new Map<string, SelectedSeat>();

        // Process existing seats that don't conflict with new selections
        seats.forEach(existingSeat => {
          const key = `${existingSeat.segmentId}-${existingSeat.passengerIndex}`;
          if (!seatMap.has(key)) {
            seatMap.set(key, existingSeat);
          }
        });

        // Add or replace with new seat selections
        seats.forEach(newSeat => {
          const key = `${newSeat.segmentId}-${newSeat.passengerIndex}`;
          seatMap.set(key, newSeat);
        });

        return {
          selectedSeats: Array.from(seatMap.values())
        };
      } catch (error) {
        console.error("Error updating multiple seats:", error);
        return state; // Return unchanged state on error
      }
    }),

  getSeatsForPassenger: (passengerIndex: number) => {
    try {
      return get().selectedSeats.filter(seat => seat.passengerIndex === passengerIndex);
    } catch (error) {
      console.error("Error getting seats for passenger:", error);
      return [];
    }
  },

  getSeatsForSegment: (segmentId: string) => {
    try {
      return get().selectedSeats.filter(seat => seat.segmentId === segmentId);
    } catch (error) {
      console.error("Error getting seats for segment:", error);
      return [];
    }
  },

  // New utility function
  removeEmptyFields(obj: any) {
    if (typeof obj === "object" && obj !== null) {
      // Loop through the object keys
      for (const key in obj) {
        if (obj.hasOwnProperty(key)) {
          const value = obj[key];

          // Recursively check nested objects or arrays
          if (typeof value === "object" && value !== null) {
            useFlightBookingStore.getState().removeEmptyFields(value);

            // If the object is empty after removing empty fields, delete the key
            if (Array.isArray(value)) {
              // Remove the array if it's empty
              obj[key] = value.filter(
                (item) => item !== "" && item !== null && item !== undefined
              );
              if (obj[key].length === 0) {
                delete obj[key];
              }
            } else if (Object.keys(value).length === 0) {
              delete obj[key];
            }
          } else if (value === "" || value === null || value === undefined) {
            // Delete the key if value is empty string, null, or undefined
            delete obj[key];
          }
        }
      }
    }

    return obj; // Return the cleaned object
  },
  setSeatMapError: (error: Error | null) => {
    set({ seatMapError: error });
  },
  setIndexValue: (index: number) => {
    set({ indexValue: index });
  },

  setIsViewOpen: (isOpen: boolean) => {
    set({ isViewOpen: isOpen });
  },

  setIsEmailOpen: (isOpen: boolean) => {
    set({ isEmailOpen: isOpen });
  },

  setIsWhatsAppOpen: (isOpen: boolean) => {
    set({ isWhatsAppOpen: isOpen });
  },

  setViewData: (data: any) => {
    set({ viewData: data });
  },

  setshareMails: (mails: any) => {
    set({ shareMails: mails });
  },
}));

export default useFlightBookingStore;
